import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { ReactComponent as RouteIcon } from "../../asset/icons/customer-club-route-icon.svg";
import { formatCount } from "./Components/UserManagement/helpers";
import {
    EARNED_COLOR,
    SPENT_COLOR,
    TOTAL_COLOR,
    TOTAL_SERIES_KEY,
    coinsTrendChartData,
    memberCompositionData,
    memberTrendChartData,
} from "./chartHelpers";

const CHART_MARGIN = { top: 8, right: 4, left: -28, bottom: 0 };

function ChartCard({ title, children, empty, loading }) {
    const { t } = useTranslation();
    return (
        <div className="chart">
            <div className="title">
                <RouteIcon aria-hidden="true" />
                <span>{title}</span>
            </div>
            {loading ? (
                <div className="no-data-alert" aria-busy="true" />
            ) : empty ? (
                <div className="no-data-alert">
                    {t("Organizational.CustomerClub.Not.Found")}
                </div>
            ) : (
                children
            )}
        </div>
    );
}

function ChartLegend({ items }) {
    if (!items.length) return null;
    return (
        <ul className="chart-legend">
            {items.map((item) => (
                <li key={item.key}>
                    <span style={{ background: item.color }} aria-hidden="true" />
                    {item.label}
                    {item.value != null ? (
                        <b className="tabular-nums">{item.value}</b>
                    ) : null}
                </li>
            ))}
        </ul>
    );
}

function CompactTooltip({ active, payload, label, formatter }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="chart-tooltip">
            <strong>{label}</strong>
            <ul>
                {payload.map((entry) => (
                    <li key={entry.dataKey}>
                        <span style={{ background: entry.color }} aria-hidden="true" />
                        {entry.name}
                        <b className="tabular-nums">
                            {formatter(entry.value)}
                        </b>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function ParticipationSidebar({ summary }) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language?.startsWith("en") ? "en-US" : "fa-IR";
    const ungrouped = t("Organizational.CustomerClub.Members.Report.Ungrouped");
    const totalLabel = t("Organizational.CustomerClub.Members.Total");
    const formatValue = (value) => formatCount(value, locale);

    const trend = useMemo(
        () =>
            memberTrendChartData(
                summary?.joined_by_month,
                summary?.groups,
                i18n.language
            ),
        [summary, i18n.language]
    );
    const composition = useMemo(
        () => memberCompositionData(summary?.groups),
        [summary]
    );
    const coins = useMemo(
        () => coinsTrendChartData(summary?.coins_by_month, i18n.language),
        [summary, i18n.language]
    );

    const trendLegend = [
        { key: TOTAL_SERIES_KEY, color: TOTAL_COLOR, label: totalLabel },
        ...trend.series.map((item) => ({
            key: item.key,
            color: item.color,
            label: item.name || ungrouped,
        })),
    ];
    const compositionTotal = composition.reduce(
        (sum, slice) => sum + slice.value,
        0
    );
    const pieData = composition.map((slice) => ({
        ...slice,
        name: slice.name || ungrouped,
    }));
    const compositionLegend = pieData.map((slice) => ({
        key: slice.key,
        color: slice.color,
        label: slice.name,
        value: formatValue(slice.value),
    }));
    const coinsLegend = [
        {
            key: "earned",
            color: EARNED_COLOR,
            label: t("Organizational.CustomerClub.Coins.Earned"),
        },
        {
            key: "spent",
            color: SPENT_COLOR,
            label: t("Organizational.CustomerClub.Coins.Spent"),
        },
    ];

    const loading = summary == null;

    return (
        <>
            <ChartCard
                title={t("Organizational.CustomerClub.MembersChangingRate.Label")}
                loading={loading}
                empty={!trend.data.length}
            >
                <div>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trend.data} margin={CHART_MARGIN}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8edf5" />
                            <XAxis
                                dataKey="label"
                                tickLine={false}
                                axisLine={false}
                                fontSize={9}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                allowDecimals={false}
                                tickLine={false}
                                axisLine={false}
                                fontSize={9}
                                tickMargin={8}
                            />
                            <Tooltip
                                content={
                                    <CompactTooltip formatter={formatValue} />
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey={TOTAL_SERIES_KEY}
                                name={totalLabel}
                                stroke={TOTAL_COLOR}
                                strokeWidth={2.25}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                            {trend.series.map((item) => (
                                <Line
                                    key={item.key}
                                    type="monotone"
                                    dataKey={item.key}
                                    name={item.name || ungrouped}
                                    stroke={item.color}
                                    strokeWidth={1.5}
                                    strokeDasharray="4 3"
                                    dot={false}
                                    activeDot={{ r: 3 }}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                    <ChartLegend items={trendLegend} />
                </div>
            </ChartCard>

            <ChartCard
                title={t("Organizational.CustomerClub.MembersComposition.Label")}
                loading={loading}
                empty={!composition.length}
            >
                <div>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                innerRadius={42}
                                outerRadius={58}
                                paddingAngle={3}
                                stroke="#fff"
                                strokeWidth={1}
                            >
                                {pieData.map((slice) => (
                                    <Cell key={slice.key} fill={slice.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                formatter={(value, name) => [
                                    `${formatValue(value)} (${
                                        compositionTotal
                                            ? Math.round(
                                                  (Number(value) /
                                                      compositionTotal) *
                                                      100
                                              )
                                            : 0
                                    }%)`,
                                    name || ungrouped,
                                ]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <ChartLegend items={compositionLegend} />
                </div>
            </ChartCard>

            <ChartCard
                title={t("Organizational.CustomerClub.CoinsTrend.Label")}
                loading={loading}
                empty={!coins.length}
            >
                <div>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={coins} margin={CHART_MARGIN}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e8edf5" />
                            <XAxis
                                dataKey="label"
                                tickLine={false}
                                axisLine={false}
                                fontSize={9}
                                interval="preserveStartEnd"
                            />
                            <YAxis
                                allowDecimals={false}
                                tickLine={false}
                                axisLine={false}
                                fontSize={9}
                                tickMargin={8}
                            />
                            <Tooltip
                                content={
                                    <CompactTooltip formatter={formatValue} />
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey="earned"
                                name={t(
                                    "Organizational.CustomerClub.Coins.Earned"
                                )}
                                stroke={EARNED_COLOR}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="spent"
                                name={t(
                                    "Organizational.CustomerClub.Coins.Spent"
                                )}
                                stroke={SPENT_COLOR}
                                strokeWidth={2}
                                dot={false}
                                activeDot={{ r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    <ChartLegend items={coinsLegend} />
                </div>
            </ChartCard>
        </>
    );
}
